import React, { useMemo, useState } from 'react';
import { FaPlus, FaTimes } from 'react-icons/fa';
import { useConfirm } from '../contexts/ConfirmContext';
import TarihAlani from './TarihAlani';
import { parseIso } from './isGunuHesap';
import { DINI_KAPSAM, bayramGunleri, bayramTipAdi } from './diniBayramlar';
import { egikTarih } from './formatlayicilar';

const TUR_KISA = { ramazan: 'Ramazan', kurban: 'Kurban' };

/**
 * Eksik dinî bayram yönetimi (Ayarlar penceresinde). Kapsam dışı (2018–2032 dışı)
 * yıllar için bayramın yalnızca ilk günü girilir; Ramazan 3, Kurban 4 gün işlenir.
 *
 * Mobil-öncelikli: ekleme formu dar ekranda dikey yığılır; liste tek sütun, geniş
 * ekranda iki sütundur ve yıl vurgusuyla uzun listede de taranabilir kalır.
 *
 * Props: ekBayramlar, onEkle(tip, ilkGun), onSil(index). Ekleme/silme anında kaydedilir.
 */
function EkBayramYonetici({ ekBayramlar, onEkle, onSil }) {
  const [tip, setTip] = useState('ramazan');
  const [iso, setIso] = useState('');
  const [uyari, setUyari] = useState('');

  // Gelecek yıl bayramları da takvimden seçilebilsin diye uzak bir üst sınır.
  const farFuture = useMemo(() => new Date(2200, 11, 31), []);
  const confirm = useConfirm();

  const silOnayli = async (b, i) => {
    const yil = parseIso(b.ilkGun).getFullYear();
    const onay = await confirm({
      title: 'Dinî bayram silinsin mi?',
      message: `${bayramTipAdi(b.tip)} · ${yil} kaldırılacak.`,
      confirmText: 'Sil',
      cancelText: 'Vazgeç',
      tone: 'danger',
    });
    if (onay) onSil(i);
  };

  const ekle = () => {
    const d = parseIso(iso);
    if (!d) {
      setUyari('Lütfen bayramın ilk günü için geçerli bir tarih girin (GG/AA/YYYY).');
      return;
    }
    const yil = d.getFullYear();
    if (yil >= DINI_KAPSAM.min && yil <= DINI_KAPSAM.max) {
      setUyari(`${yil} yılı sistemde zaten tanımlı (${DINI_KAPSAM.min}–${DINI_KAPSAM.max}). Bu yıla bayram eklenemez.`);
      return;
    }
    if (ekBayramlar.some((b) => b.tip === tip && parseIso(b.ilkGun).getFullYear() === yil)) {
      setUyari(`${yil} yılı için ${bayramTipAdi(tip)} zaten eklenmiş.`);
      return;
    }
    setUyari('');
    onEkle(tip, iso);
    setIso('');
  };

  return (
    <div>
      <p className="mb-3 text-xs leading-relaxed text-slate-500">
        Sistemde <strong className="text-slate-700">{DINI_KAPSAM.min}–{DINI_KAPSAM.max}</strong> yılları tanımlı.
        Kapsam dışı bir yıl için bayramın <strong className="text-slate-700">ilk gününü</strong> girin;
        Ramazan 3, Kurban 4 gün işlenir.
      </p>

      {/* Ekleme paneli */}
      <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 sm:p-3.5">
        <div className="app-segment w-full">
          {[
            { val: 'ramazan', ad: 'Ramazan', alt: '3 gün' },
            { val: 'kurban', ad: 'Kurban', alt: '4 gün' },
          ].map((o) => (
            <button
              key={o.val}
              type="button"
              onClick={() => setTip(o.val)}
              className={`app-segment-item flex flex-1 flex-col items-center leading-tight !py-1.5 ${tip === o.val ? 'app-segment-item-active' : ''}`}
            >
              <span className="font-semibold">{o.ad} Bayramı</span>
              <span className="text-[10px] text-slate-400">{o.alt}</span>
            </button>
          ))}
        </div>

        <label className="mb-1.5 mt-3 block text-xs font-semibold text-slate-600">Bayramın ilk günü</label>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <TarihAlani valueIso={iso} onChange={setIso} maxDate={farFuture} />
          </div>
          <button
            type="button"
            onClick={ekle}
            className="btn-success w-full justify-center !px-4 sm:w-auto sm:flex-none"
          >
            <FaPlus className="h-3 w-3" /> Ekle
          </button>
        </div>

        {uyari ? (
          <div className="mt-2.5 rounded-lg bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-800 ring-1 ring-inset ring-amber-200/70">
            {uyari}
          </div>
        ) : null}
      </div>

      {/* Liste başlığı + sayaç */}
      <div className="mb-2 mt-4 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Eklenenler</span>
        {ekBayramlar.length ? (
          <span className="app-chip app-chip-accent !py-0.5 text-[11px]">{ekBayramlar.length}</span>
        ) : null}
      </div>

      {ekBayramlar.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-200 px-3 py-4 text-center text-xs text-slate-400">
          Henüz ek bayram eklenmedi.
        </div>
      ) : (
        <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {ekBayramlar.map((b, i) => {
            const gunler = bayramGunleri(b.tip, b.ilkGun);
            const son = gunler[gunler.length - 1];
            const yil = parseIso(b.ilkGun).getFullYear();
            return (
              <li
                key={`${b.tip}-${b.ilkGun}`}
                className="flex items-center gap-2.5 rounded-lg border border-slate-200 bg-white py-2 pl-2.5 pr-1.5 text-sm"
              >
                <span
                  className={`h-2.5 w-2.5 flex-none rounded-full ${b.tip === 'kurban' ? 'bg-amber-500' : 'bg-emerald-600'}`}
                />
                <span className="flex min-w-0 flex-col leading-tight">
                  <span className="truncate font-semibold text-slate-800">
                    {TUR_KISA[b.tip]} <span className="font-normal text-slate-400">· {yil}</span>
                  </span>
                  <span className="app-num truncate text-[11px] text-slate-400">
                    {egikTarih(b.ilkGun)} – {egikTarih(son)} · {gunler.length} gün
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => silOnayli(b, i)}
                  className="ml-auto flex h-7 w-7 flex-none items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition-colors hover:border-rose-200 hover:bg-rose-50 hover:text-rose-600"
                  aria-label="Sil"
                  title="Sil"
                >
                  <FaTimes className="h-3 w-3" />
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default EkBayramYonetici;
